import {
  ERROR_CATEGORIES,
  ERROR_CODES,
  ERROR_COPY,
  UX_HINTS,
} from '@/errors/taxonomy';

const isTimeout = error =>
  error?.code === 'ECONNABORTED' || error?.message?.includes('timeout');

const statusToCategory = status => {
  if (!status) return ERROR_CATEGORIES.Unknown;
  if (status >= 500) return ERROR_CATEGORIES.Server;
  if (status === 401) return ERROR_CATEGORIES.Auth;
  if (status === 403) return ERROR_CATEGORIES.Access;
  if (status === 404) return ERROR_CATEGORIES.NotFound;
  if (status === 409) return ERROR_CATEGORIES.Conflict;
  if (status === 422 || status === 400) return ERROR_CATEGORIES.Validation;
  if (status === 429) return ERROR_CATEGORIES.RateLimit;
  return ERROR_CATEGORIES.Unknown;
};

const categoryToCode = (category, status) => {
  switch (category) {
    case ERROR_CATEGORIES.Network:
      return navigator.onLine
        ? ERROR_CODES.NETWORK_TIMEOUT
        : ERROR_CODES.NETWORK_OFFLINE;
    case ERROR_CATEGORIES.Auth:
      if (status === 401) return ERROR_CODES.AUTH_UNAUTHENTICATED;
      return ERROR_CODES.AUTH_EXPIRED;
    case ERROR_CATEGORIES.Access:
      return ERROR_CODES.ACCESS_FORBIDDEN;
    case ERROR_CATEGORIES.Validation:
      return ERROR_CODES.VALIDATION_FAILED;
    case ERROR_CATEGORIES.NotFound:
      return ERROR_CODES.NOT_FOUND;
    case ERROR_CATEGORIES.Conflict:
      return ERROR_CODES.CONFLICT;
    case ERROR_CATEGORIES.RateLimit:
      return ERROR_CODES.RATE_LIMIT;
    case ERROR_CATEGORIES.Server:
      return ERROR_CODES.SERVER_ERROR;
    default:
      return ERROR_CODES.UNKNOWN;
  }
};

const categoryToUxHint = category => {
  switch (category) {
    case ERROR_CATEGORIES.Auth:
      return UX_HINTS.REDIRECT_LOGIN;
    case ERROR_CATEGORIES.Network:
    case ERROR_CATEGORIES.Server:
    case ERROR_CATEGORIES.RateLimit:
      return UX_HINTS.RETRY;
    case ERROR_CATEGORIES.Access:
      return UX_HINTS.CONTACT_SUPPORT;
    default:
      return UX_HINTS.NONE;
  }
};

export function normalizeError(error, { requestId: overrideRequestId } = {}) {
  if (isAppError(error)) {
    return error;
  }
  if (!error) {
    return {
      category: ERROR_CATEGORIES.Unknown,
      code: ERROR_CODES.UNKNOWN,
      message: ERROR_COPY[ERROR_CODES.UNKNOWN],
      details: null,
      requestId: overrideRequestId || null,
      status: undefined,
      original: error,
      uxHint: UX_HINTS.NONE,
    };
  }

  const isNetworkError =
    error?.message === 'Network Error' ||
    error?.code === 'ERR_NETWORK' ||
    (error?.isAxiosError && !error?.response);

  if (isNetworkError) {
    const category = ERROR_CATEGORIES.Network;
    const code = navigator.onLine
      ? ERROR_CODES.NETWORK_TIMEOUT
      : ERROR_CODES.NETWORK_OFFLINE;
    return {
      category,
      code,
      message: ERROR_COPY[code],
      details: null,
      requestId:
        overrideRequestId || error?.config?.headers?.['x-request-id'] || null,
      status: undefined,
      original: error,
      uxHint: UX_HINTS.RETRY,
    };
  }

  const status = error?.response?.status;
  const requestId =
    overrideRequestId ||
    error?.response?.headers?.['x-request-id'] ||
    error?.config?.headers?.['x-request-id'] ||
    null;
  let category = statusToCategory(status);

  if (isTimeout(error)) {
    category = ERROR_CATEGORIES.Network;
  }

  let code = categoryToCode(category, status);

  const responseData = error?.response?.data;
  let details = null;
  let message = ERROR_COPY[code];

  if (responseData) {
    if (responseData?.message && typeof responseData.message === 'string') {
      message = responseData.message;
    }

    if (responseData?.error && typeof responseData.error === 'string') {
      message = responseData.error;
    }

    if (responseData?.errors && typeof responseData.errors === 'object') {
      details = responseData.errors;
    }
  }

  if (!message) {
    message = error?.message || ERROR_COPY[ERROR_CODES.UNKNOWN];
  }

  return {
    category,
    code,
    message,
    details,
    requestId,
    status,
    original: error,
    uxHint: categoryToUxHint(category),
  };
}

export function isAppError(value) {
  return (
    value && typeof value === 'object' && 'code' in value && 'category' in value
  );
}
